import { toJS, runInAction, makeObservable, observable, action, computed } from 'mobx';
import {
    IActivityBaseComponent,
    IActivitySnapshot,
    E_COMPONENT_TYPE,
    IActivityRecordChartDataSnapshot,
    IActivityChartComponent,
    IActivityMapComponent,
    IMapNote,
    E_CHART_GROUP_IDENTIFIERS,
    E_RISK_TYPE,
    IActivityHeatmapComponent,
    INote,
} from '../../services/lib/APIServiceInterfaces';
import ColorStack from '../../utils/ColorStack';
import {
    ActivityChartNoteStore,
    IActivityChartNoteStoreProps,
    ActivityMapNoteStore,
    ActivityHeatmapNoteStore,
} from './InsightsNoteStore';
import { generateID } from '../../utils/generalUtils';
import rootStore from '../../RootStore';
import moment from 'moment';
import { VIEW } from '../../interfaces';
import { COMPOSITE_DATA_INTERVAL } from '../../components/GQChart/ChartStore';

export class ActivityBaseComponentStore implements IActivityBaseComponent {
    public id: string;
    public type: E_COMPONENT_TYPE;
    public snapshot: IActivitySnapshot;
    public readonly disableEdit?: boolean;
    public imageURL?: string;
    public thumbnailURL?: string;
    public csvURL?: string;
    public name: string;
    public countriesColorStack: ColorStack<number> = null;
    public dimensionsColorStack: ColorStack<number> = null;
    public activeClientFacingIndicatorsColorStack: ColorStack<number>;
    public htmlKey: string;

    constructor(props: IActivityBaseComponent) {
        this.htmlKey = props.htmlKey || `html-${generateID()}`;
        this.type = props.type;
        this.snapshot = props.snapshot;
        this.disableEdit = props.disableEdit;
        this.id = props.id;
        this.imageURL = props.imageURL;
        this.thumbnailURL = props.thumbnailURL;
        this.csvURL = props.csvURL;
        this.name = props.name;
        if (this.type !== E_COMPONENT_TYPE.CUSTOM) {
            this.countriesColorStack = ColorStack.fromSerialize(
                this.snapshot.countries.colorsStack
            );
            this.dimensionsColorStack = ColorStack.fromSerialize(
                this.snapshot.risks.colorsStack.dimensionsStack
            );
            this.activeClientFacingIndicatorsColorStack = ColorStack.fromSerialize(
                this.snapshot.risks.colorsStack.clientFacingIndicatorsStack
            );
        }
    }

    public get canSnapshotBeRestored() {
        return true;
    }

    public switchComponentType(type: E_COMPONENT_TYPE) {
        this.type = type;
    }

    public setSnapshot(snapshot: IActivitySnapshot) {
        this.snapshot = snapshot;
        this.countriesColorStack = ColorStack.fromSerialize(
            this.snapshot.countries.colorsStack
        );
        this.dimensionsColorStack = ColorStack.fromSerialize(
            this.snapshot.risks.colorsStack.dimensionsStack
        );
        this.activeClientFacingIndicatorsColorStack = ColorStack.fromSerialize(
            this.snapshot.risks.colorsStack.clientFacingIndicatorsStack
        );
    }

    public setURLS(urls: {
        imageURL: string;
        thumbnailURL: string;
        csvURL: string;
    }) {
        Object.assign(this, urls);
    }

    public setName(name: string) {
        this.name = name;
    }

    public toJS(): IActivityBaseComponent {
        return {
            id: toJS(this.id),
            type: toJS(this.type),
            snapshot: toJS(this.snapshot),
            disableEdit: toJS(this.disableEdit),
            imageURL: toJS(this.imageURL),
            thumbnailURL: toJS(this.thumbnailURL),
            csvURL: toJS(this.csvURL),
            name: toJS(this.name),
        };
    }

    public restoreSnapshot(): VIEW {
        if (!this.canSnapshotBeRestored) {
            return; // perhaps raise exception or something?
        }
        if (
            rootStore.risksStore.checkIfRiskDisabled(
                this.snapshot.riskType,
                this.snapshot.risks.values
            )
        ) {
            alert('cannot restore');
            return; // perhaps raise exception or something?
        }
        const snapshot = toJS(this.snapshot);
        runInAction(() => {
            if (this.type === E_COMPONENT_TYPE.CHART) {
                const currencyCountries = (this as IActivityChartComponent).data
                    .filter(
                        d =>
                            d.chartIdentifier ===
                            E_CHART_GROUP_IDENTIFIERS.CURRENCY_EXCHANGE
                    )
                    .map(d => d.country);
                rootStore.risksStore.setCurrencyCountries(currencyCountries);
            }
            rootStore.risksStore.projectionDataOn =
                snapshot.risks.projectionDataOn !== undefined
                    ? snapshot.risks.projectionDataOn
                    : rootStore.risksStore.projectionDataOn;
            (rootStore.risksStore.riskChartMode =
                snapshot.risks.riskChartMode !== undefined
                    ? snapshot.risks.riskChartMode
                    : rootStore.risksStore.riskChartMode),
                rootStore.risksStore.overrideGlobalIndicators(
                    snapshot.risks.activeGlobalIndicators || []
                );
            rootStore.risksStore.setCurrentRiskType(snapshot.riskType);
            rootStore.risksStore.currentStore.overrideValues(
                ...snapshot.risks.values
            );
            rootStore.countryStore.currentStore.overrideValues(
                ...snapshot.countries.values
            );
            rootStore.chartStore.setState({
                virtualToday: moment(snapshot.virtualToday),
                baseDate: moment(snapshot.baseDate),
                rangeLeft: snapshot.rangeLeft,
                rangeRight: snapshot.rangeRight,
                showLines: snapshot.showLines,
                showCircles: snapshot.showCircles,
                showVirtualToday:
                    snapshot.showVirtualToday !== undefined
                        ? snapshot.showVirtualToday
                        : rootStore.chartStore.showVirtualToday,
                compositeDataInterval:
                    snapshot.compositeDataInterval ??
                    COMPOSITE_DATA_INTERVAL.DAILY,
            });
        });
        return this.getComponentViewType();
    }

    public getComponentViewType(): VIEW {
        switch (this.type) {
            case E_COMPONENT_TYPE.CHART:
                return this.snapshot.riskType === E_RISK_TYPE.CONTINGENT
                    ? VIEW.CONTINGENT_GRAPH
                    : this.snapshot.countries.isMultiSelect
                    ? VIEW.WORLD_GRAPH
                    : VIEW.COUNTRY_GRAPH;
            case E_COMPONENT_TYPE.MAP:
                return VIEW.RISK_VIEW;
            case E_COMPONENT_TYPE.DELTA:
                return VIEW.SORT_BY_DELTA;
            case E_COMPONENT_TYPE.CR_DELTA:
                return VIEW.SORT_BY_CONTINGENT_DELTA;
            case E_COMPONENT_TYPE.CR_RISK:
                return VIEW.SORT_BY_CONTINGENT_RISK;
            case E_COMPONENT_TYPE.RISK:
                return VIEW.SORT_BY_RISK;
            case E_COMPONENT_TYPE.CONTINGENT_CHART:
                return VIEW.CONTINGENT_GRAPH;
            default:
                return null;
        }
    }
}

type ActivitChartComponentStoreType =
    | E_COMPONENT_TYPE.CHART
    | E_COMPONENT_TYPE.CONTINGENT_CHART;
export class ActivityChartComponentStore extends ActivityBaseComponentStore {
    public type: ActivitChartComponentStoreType;
    private chartData: IActivityRecordChartDataSnapshot[] = [];
    private annotationArr: ActivityChartNoteStore[];
    private deletedAnnotation: {
        annotation: ActivityChartNoteStore;
        index: number;
    };
    constructor(component: IActivityChartComponent) {
        super({
            id: component.id,
            snapshot: component.snapshot,
            type: component.type,
            disableEdit: false,
            imageURL: component.imageURL,
            thumbnailURL: component.thumbnailURL,
            csvURL: component.csvURL,
            name: component.name,
            htmlKey: component.htmlKey,
        });
        this.type = component.type;
        this.chartData = component.data;
        this.annotationArr = component.annotations
            ? component.annotations.map(a => new ActivityChartNoteStore(a))
            : [];
    }

    public clone(keepId: boolean = false) {
        return new ActivityChartComponentStore({
            annotations: this.annotationArr.map(c => c.toJS()),
            data: toJS(this.chartData),
            ...super.toJS(),
            type: E_COMPONENT_TYPE.CHART,
            id: keepId ? toJS(this.id) : generateID(),
        });
    }

    public addAnnotation(annotation: IActivityChartNoteStoreProps) {
        this.annotationArr.push(new ActivityChartNoteStore(annotation));
    }
    public removeAnnotation(index: number) {
        this.deletedAnnotation = {
            annotation: this.annotationArr[index],
            index,
        };
        this.annotationArr = [
            ...this.annotationArr.slice(0, index),
            ...this.annotationArr.slice(index + 1, this.annotationArr.length),
        ];
    }
    public undoRemoveAnnotation() {
        if (this.deletedAnnotation) {
            const { annotation, index } = this.deletedAnnotation;
            this.annotationArr.splice(index, 0, annotation);
            this.deletedAnnotation = null;
        }
    }
    public get annotations() {
        return this.annotationArr;
    }
    public setData(data: IActivityRecordChartDataSnapshot[]) {
        this.chartData = data;
    }
    public get data() {
        return this.chartData;
    }

    public toJS(): IActivityChartComponent {
        return {
            data: toJS(this.chartData),
            annotations: toJS(this.annotationArr.map(c => c.toJS())),
            id: toJS(this.id),
            type: toJS(this.type),
            snapshot: toJS(this.snapshot),
            name: toJS(this.name),
            htmlKey: toJS(this.htmlKey),
        };
    }
}

type ActivitHeatmapComponentStoreType = E_COMPONENT_TYPE.HEATMAP;

export class ActivityHeatmapComponentStore extends ActivityBaseComponentStore {
    public type: ActivitHeatmapComponentStoreType;
    private chartData: IActivityRecordChartDataSnapshot[] = [];
    private annotationArr: ActivityHeatmapNoteStore[];
    private deletedAnnotation: {
        annotation: ActivityHeatmapNoteStore;
        index: number;
    };
    constructor(component: IActivityHeatmapComponent) {
        super({
            id: component.id,
            snapshot: component.snapshot,
            type: component.type,
            disableEdit: false,
            imageURL: component.imageURL,
            thumbnailURL: component.thumbnailURL,
            csvURL: component.csvURL,
            name: component.name,
            htmlKey: component.htmlKey,
        });
        this.type = component.type;
        this.annotationArr = component.annotations
            ? component.annotations.map(a => new ActivityHeatmapNoteStore(a))
            : [];
    }

    public clone(keepId: boolean = false) {
        return new ActivityHeatmapComponentStore({
            annotations: this.annotationArr.map(c => c.toJS()),
            ...super.toJS(),
            type: E_COMPONENT_TYPE.HEATMAP,
            id: keepId ? toJS(this.id) : generateID(),
        });
    }

    public addAnnotation(annotation: INote) {
        this.annotationArr.push(new ActivityHeatmapNoteStore(annotation));
    }
    public removeAnnotation(index: number) {
        this.deletedAnnotation = {
            annotation: this.annotationArr[index],
            index,
        };
        this.annotationArr = [
            ...this.annotationArr.slice(0, index),
            ...this.annotationArr.slice(index + 1, this.annotationArr.length),
        ];
    }
    public undoRemoveAnnotation() {
        if (this.deletedAnnotation) {
            const { annotation, index } = this.deletedAnnotation;
            this.annotationArr.splice(index, 0, annotation);
            this.deletedAnnotation = null;
        }
    }
    public get annotations() {
        return this.annotationArr;
    }
    public setData(data: IActivityRecordChartDataSnapshot[]) {
        this.chartData = data;
    }
    public get data() {
        return this.chartData;
    }

    public toJS(): IActivityHeatmapComponent {
        return {
            data: toJS(this.chartData),
            annotations: toJS(this.annotationArr.map(c => c.toJS())),
            id: toJS(this.id),
            type: toJS(this.type),
            snapshot: toJS(this.snapshot),
            name: toJS(this.name),
            htmlKey: toJS(this.htmlKey),
        };
    }
}

type ActivityMapComponentStoreType =
    | E_COMPONENT_TYPE.MAP
    | E_COMPONENT_TYPE.DELTA
    | E_COMPONENT_TYPE.CR_DELTA
    | E_COMPONENT_TYPE.CR_RISK
    | E_COMPONENT_TYPE.RISK
    | E_COMPONENT_TYPE.CONTINGENT_MAP;
export class ActivityMapComponentStore extends ActivityBaseComponentStore {
    public type: ActivityMapComponentStoreType;
    private chartData: IActivityRecordChartDataSnapshot[] = [];
    private allAnnotations: {
        [key: string]: ActivityMapNoteStore[];
    } = {
        [E_COMPONENT_TYPE.MAP]: [],
        [E_COMPONENT_TYPE.DELTA]: [],
        [E_COMPONENT_TYPE.RISK]: [],
        [E_COMPONENT_TYPE.CONTINGENT_MAP]: [],
        [E_COMPONENT_TYPE.CR_DELTA]: [],
        [E_COMPONENT_TYPE.CR_RISK]: [],
    };
    private deletedAnnotation: {
        annotation: ActivityMapNoteStore;
        index: number;
    };
    constructor(props: IActivityMapComponent) {
        super(props);
        this.type = props.type;
        this.chartData = props.data;
        this.annotationArr = props.annotations
            ? props.annotations.map(a => new ActivityMapNoteStore(a))
            : [];
        makeObservable<ActivityMapComponentStore, 'allAnnotations'>(this, {
            allAnnotations: observable,
            annotations: computed,
            addAnnotation: action,
            removeAnnotation: action,
            undoRemoveAnnotation: action,
        });
    }

    public clone(keepId: boolean = false) {
        return new ActivityMapComponentStore({
            ...super.toJS(),
            type: this.type,
            data: toJS(this.chartData),
            annotations: this.annotationArr.map(c => c.toJS()),
            id: keepId ? toJS(this.id) : generateID(),
        });
    }

    public addAnnotation(annotation: IMapNote) {
        this.annotationArr.push(new ActivityMapNoteStore(annotation));
    }
    public removeAnnotation(index: number) {
        this.deletedAnnotation = {
            annotation: this.annotationArr[index],
            index,
        };
        this.annotationArr = [
            ...this.annotationArr.slice(0, index),
            ...this.annotationArr.slice(index + 1, this.annotationArr.length),
        ];
    }
    public undoRemoveAnnotation() {
        if (this.deletedAnnotation) {
            const { annotation, index } = this.deletedAnnotation;
            this.annotationArr.splice(index, 0, annotation);
            this.deletedAnnotation = null;
        }
    }
    public get annotations() {
        return this.allAnnotations[this.type];
    }
    public setData(data: IActivityRecordChartDataSnapshot[]) {
        this.chartData = data;
    }

    public get data() {
        return this.chartData;
    }
    // notice it's private, no because called already from action or consturctor
    private set annotationArr(arr: ActivityMapNoteStore[]) {
        this.allAnnotations[this.type] = arr;
    }

    private get annotationArr() {
        return this.allAnnotations[this.type];
    }

    public toJS(): IActivityMapComponent {
        return {
            data: toJS(this.chartData),
            annotations: toJS(this.annotationArr.map(c => c.toJS())),
            id: toJS(this.id),
            type: toJS(this.type) as
                | E_COMPONENT_TYPE.MAP
                | E_COMPONENT_TYPE.DELTA
                | E_COMPONENT_TYPE.RISK
                | E_COMPONENT_TYPE.CR_DELTA
                | E_COMPONENT_TYPE.CR_RISK
                | E_COMPONENT_TYPE.CONTINGENT_MAP,
            snapshot: toJS(this.snapshot),
            name: toJS(this.name),
            htmlKey: toJS(this.htmlKey),
        };
    }
}

export class ActivityCustomImageComponentStore extends ActivityBaseComponentStore {
    public type: E_COMPONENT_TYPE.CUSTOM = E_COMPONENT_TYPE.CUSTOM;

    public get canSnapshotBeRestored() {
        return false;
    }
}
export type ActivityComponentStore =
    | ActivityMapComponentStore
    | ActivityChartComponentStore
    | ActivityCustomImageComponentStore
    | ActivityHeatmapComponentStore;

export function activityComponentStoreFromActivityComponent(
    component: IActivityBaseComponent
): ActivityComponentStore {
    switch (component.type) {
        case E_COMPONENT_TYPE.CHART:
            return new ActivityChartComponentStore(
                component as IActivityChartComponent
            );
        case E_COMPONENT_TYPE.CUSTOM:
            return new ActivityCustomImageComponentStore(component);
        case E_COMPONENT_TYPE.HEATMAP:
            return new ActivityHeatmapComponentStore(
                component as IActivityHeatmapComponent
            );
        default:
            return new ActivityMapComponentStore(
                component as IActivityMapComponent
            );
    }
}
